import { useCallback, useState } from "react";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";

import {
  FormControl,
  FormGroup,
  FormLabel,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Box,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
} from "@material-ui/core";

// @material-ui/icons components
import { WhatsApp as WhatsAppIcon, Email } from "@material-ui/icons";

// Karsten
import componentStyles from "assets/theme/views/index"; // "assets/theme/views/admin/react-bs-table";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import useForm from "hooks/form";
import MaskAndValid from "utils/maskAndValid";
import Api from "lib/api/httpRequestHelper";
import useLogs from "hooks/logs";

const useStyles = makeStyles(componentStyles);

const validateForm = (values) => {
  const { tipoExportacao, assunto, email, telefone } = values;
  const errors = {};

  switch (tipoExportacao) {
    case "Whatsapp":
      if (MaskAndValid.isNullOrEmpty(telefone)) {
        errors.email = "Telefone é obrigatório";
      } else if (!MaskAndValid.validatePhone(telefone)) {
        errors.telefone = "Telefone inválido";
      }

      break;
    case "E-mail":
      if (MaskAndValid.isNullOrEmpty(assunto)) {
        errors.email = "E-mail é obrigatório";
      } else if (!MaskAndValid.validateEmail(email)) {
        errors.email = "E-mail inválido";
      }

      if (MaskAndValid.isNullOrEmpty(assunto)) {
        errors.assunto = "Assunto é obrigatório";
      }

      break;
    default:
      break;
  }

  return errors;
};

export default function DialogExportFinancialTitles({
  open,
  handleClose,
  titles,
  storePhone,
}) {
  const classes = useStyles();
  const snackbar = useSnackbar();
  const { setLocalStorageApiProblem } = useLogs();

  const [exportando, setExportando] = useState(false);
  const { values, handleChange, errors } = useForm(
    {
      tipoExportacao: "Download",
      assunto: "",
      email: "",
      telefone: storePhone ? MaskAndValid.maskPhone(storePhone) : "",
    },
    validateForm
  );

  const exportByEmail = useCallback(
    async (titles, email, assunto) => {
      setExportando(true);

      await Api.get(
        "send-email-list-financial-docs",
        "financialdocument",
        "?numerosTitulos=" +
          titles.map((title) => title.NumeroTitulo).join(",") +
          "&email=" +
          email +
          "&assunto=" +
          assunto,
        true
      )
        .then(({ Success }) => {
          if (Success) {
            snackbar.showMessage("Boletos exportados por e-mail!", {
              severity: "success",
            });

            handleClose();
          }
        })
        .catch((error) => {
          snackbar.showMessage(error, {
            severity: "error",
          });

          setExportando(false);
          setLocalStorageApiProblem({
            idMenuAccess: "108",
            screen: "Relatório de Acompanhamento Títulos",
            problem: `Erro na chamada do endpoint send-email-list-financial-docs - ${error}`,
          });
        });
    },
    [handleClose, snackbar]
  );

  const exportByDownload = useCallback(
    async (titles) => {
      setExportando(true);

      await Api.get(
        "batch-download-financial-docs",
        "financialdocument",
        "?numerosTitulos=" + titles.map((title) => title?.NumeroTitulo),
        true
      )
        .then(({ Success, Result }) => {
          if (Success) {
            window.open(Result, "_blank");

            handleClose();
          }
        })
        .catch((error) => {
          snackbar.showMessage(error, {
            severity: "error",
          });

          setExportando(false);
          setLocalStorageApiProblem({
            idMenuAccess: "108",
            screen: "Relatório de Acompanhamento Títulos",
            problem: `Erro na chamada do endpoint batch-download-financial-docs - ${error}`,
          });
        });
    },
    [handleClose, snackbar]
  );

  const exportar = useCallback(() => {
    const { tipoExportacao, assunto, email, telefone } = values;

    switch (tipoExportacao) {
      case "Whatsapp": {
        const url =
          encodeURI(
            `https://api.whatsapp.com/send?phone=${
              "55" + telefone?.replace(/\D/g, "")
            }&text=Aqui está a lista de boletos dos títulos selecionados do *Portal Karsten* para download:
            `
          ) +
          titles.map((title) =>
            encodeURIComponent(`
Boleto do Título Financeiro nº ${title.NumeroTitulo}: ${title.UrlDownloadBoleto}
                `)
          ).join(`
            `);

        window.open(url, "_blank");
        break;
      }
      case "E-mail":
        exportByEmail(titles, email, assunto);
        break;
      default:
        exportByDownload(titles);
        break;
    }
  }, [exportByDownload, exportByEmail, titles, values]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        <span>
          <Typography variant="h3" component="h3">
            Exportar Boletos Selecionados
          </Typography>
        </span>
      </DialogTitle>
      <DialogContent>
        <FormGroup>
          <FormLabel>
            Selecione a forma que deseja exportar os boletos selecionados:
          </FormLabel>
          <FormControl variant="outlined" fullWidth>
            <Select
              defaultValue={"Download"}
              name="tipoExportacao"
              value={values.tipoExportacao}
              onChange={handleChange}
            >
              <MenuItem value={"Download"}>Download</MenuItem>
              <MenuItem value={"E-mail"}>E-mail</MenuItem>
              <MenuItem value={"Whatsapp"}>Whatsapp</MenuItem>
            </Select>
          </FormControl>
        </FormGroup>
        {values.tipoExportacao === "E-mail" ? (
          <>
            <FormGroup>
              <FormLabel>Assunto</FormLabel>
              <TextField
                name="assunto"
                type="text"
                fullWidth
                variant="outlined"
                helperText={errors["assunto"]}
                value={values.assunto}
                onChange={handleChange}
                onInput={(e) => {
                  e.target.value = e.target.value.toString().slice(0, 200);
                }}
              />
            </FormGroup>
            <FormGroup>
              <FormLabel>E-mail</FormLabel>
              <TextField
                fullWidth
                variant="outlined"
                type="email"
                name="email"
                autoComplete="email"
                value={values.email}
                helperText={errors["email"]}
                onChange={handleChange}
                onInput={(e) =>
                  (e.target.value = e.target.value.toString().slice(0, 50))
                }
                disabled={exportando}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email />
                    </InputAdornment>
                  ),
                }}
              />
            </FormGroup>
          </>
        ) : values.tipoExportacao === "Whatsapp" ? (
          <>
            <FormGroup>
              <FormLabel>Telefone</FormLabel>
              <TextField
                variant="outlined"
                name="telefone"
                type="text"
                fullWidth
                autoComplete="tel"
                placeholder="Telefone"
                value={values.telefone}
                helperText={errors["telefone"]}
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "telefone",
                      value: MaskAndValid.maskPhone(e.target.value),
                    },
                  })
                }
                onInput={(e) =>
                  (e.target.value = e.target.value.toString().slice(0, 50))
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WhatsAppIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </FormGroup>
          </>
        ) : null}
      </DialogContent>
      <DialogContent hidden={!exportando}>
        <div className={classes.root}>
          <span>Exportando boletos...</span>
          <LinearProgress />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="default" disabled={exportando}>
          Cancelar
        </Button>
        <Button
          component={Box}
          onClick={exportar}
          color="default"
          variant="contained"
          disabled={Object.keys(errors).length > 0 || exportando}
        >
          Exportar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
